import React, { useState } from 'react'
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Popconfirm,
  message,
  Card,
  Tag,
  Typography,
  Row,
  Col,
  Alert,
  InputNumber,
  Upload,
  Image
} from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  FolderOutlined,
  AppstoreOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  PictureOutlined
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoriesApi, uploadApi } from '../../services/api'
import type { ColumnsType } from 'antd/es/table'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

interface Category {
  _id: string
  name: {
    en: string
    zh: string
  }
  slug: string
  description?: {
    en?: string
    zh?: string
  }
  parent?: {
    _id: string
    name: { en: string; zh: string }
  }
  level: number
  isMainCategory: boolean
  canEdit: boolean
  isActive: boolean
  sortOrder: number
  productCount: number
  image?: {
    url: string
    alt: { en: string; zh: string }
  }
  createdAt: string
  updatedAt: string
  subcategories?: Category[]
}

const AdminCategories: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [, setSelectedParent] = useState<string | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [categoryImage, setCategoryImage] = useState<{ url: string; alt: { en: string; zh: string } } | null>(null)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Fetch categories
  const { data: categoriesData, isLoading, error } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => categoriesApi.getAdminAll(),
  })

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: (data: any) => {
      console.log('Creating category with data:', data);
      return categoriesApi.create(data);
    },
    onSuccess: (response) => {
      console.log('Category created successfully:', response);
      message.success('Category created successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
      setIsModalOpen(false)
      form.resetFields()
      setSelectedParent(null)
    },
    onError: (error: any) => {
      console.error('Category creation error:', error);
      console.error('Error response:', error?.response);
      console.error('Error response data:', error?.response?.data);
      
      const errorData = error?.response?.data;
      let errorMessage = 'Failed to create category';
      
      if (errorData?.details && Array.isArray(errorData.details)) {
        errorMessage = `Validation errors: ${errorData.details.map((d: any) => `${d.field}: ${d.message}`).join(', ')}`;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
    },
  })

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      categoriesApi.update(id, data),
    onSuccess: () => {
      message.success('Category updated successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
      setIsModalOpen(false)
      form.resetFields()
      setEditingCategory(null)
      setSelectedParent(null)
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update category')
    },
  })

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.delete(id),
    onSuccess: () => {
      message.success('Category deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete category')
    },
  })

  const categories = categoriesData?.categories || []
  const mainCategories = categories.filter((cat: Category) => cat.level === 0)

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadLoading(true)
    try {
      const response = await uploadApi.uploadImage(file)
      message.success('Image uploaded successfully')
      return response.file.url
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to upload image')
      throw error
    } finally {
      setUploadLoading(false)
    }
  }

  // Handle category image upload
  const handleCategoryImageUpload = async (file: File) => {
    try {
      const imageUrl = await handleImageUpload(file)
      const imageObject = {
        url: imageUrl,
        alt: {
          en: 'Category image',
          zh: '分類圖片'
        }
      }
      setCategoryImage(imageObject)
      form.setFieldValue('image', imageObject)
    } catch (error) {
      console.error('Failed to upload category image:', error)
    }
    return false // Prevent default upload behavior
  }

  // Table columns
  const columns: ColumnsType<Category> = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 80,
      render: (image: Category['image'], record: Category) => (
        image?.url ? (
          <Image
            width={50}
            height={50}
            src={image.url}
            alt={image.alt.en || record.name.en}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7nOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg="
          />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
            <PictureOutlined className="text-gray-400 text-xs" />
          </div>
        )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: { en: string; zh: string }, record: Category) => (
        <div className="flex items-center">
          {record.level === 0 ? (
            <FolderOutlined className="mr-2 text-blue-500" />
          ) : (
            <AppstoreOutlined className="mr-2 text-green-500 ml-4" />
          )}
          <div>
            <div className="font-medium">{name.en}</div>
            <div className="text-sm text-gray-500">{name.zh}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level: number) => (
        <Tag color={level === 0 ? 'blue' : 'green'}>
          {level === 0 ? 'Main' : 'Sub'}
        </Tag>
      ),
    },
    {
      title: 'Parent',
      dataIndex: 'parent',
      key: 'parent',
      render: (parent: Category['parent']) => (
        parent ? (
          <span className="text-gray-600">{parent.name.en}</span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },

    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record: Category) => (
        <Space direction="vertical" size="small">
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
          {!record.canEdit && (
            <Tag color="orange" icon={<WarningOutlined />}>
              Protected
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Sort Order',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      render: (order: number) => <span className="text-gray-600">{order}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record: Category) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={!record.canEdit}
            title={record.canEdit ? "Edit Category" : "Protected category cannot be edited"}
          />
          <Popconfirm
            title="Are you sure you want to delete this category?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            disabled={!record.canEdit}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!record.canEdit}
              title={!record.canEdit ? "Protected category cannot be deleted" : "Delete Category"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleAdd = (parentId?: string) => {
    setEditingCategory(null)
    setSelectedParent(parentId || null)
    form.resetFields()
    if (parentId) {
      form.setFieldsValue({ parent: parentId })
    }
    setIsModalOpen(true)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setSelectedParent(category.parent?._id || null)
    setCategoryImage(category.image || null)
    form.setFieldsValue({
      name: category.name,
      description: category.description || { en: '', zh: '' },
      parent: category.parent?._id,
      isActive: category.isActive,
      sortOrder: category.sortOrder,
      image: category.image || null,
    })
    setIsModalOpen(true)
  }

  const handleDelete = (categoryId: string) => {
    deleteCategoryMutation.mutate(categoryId)
  }

  const handleSubmit = (values: any) => {
    console.log('Form submitted with values:', values);
    
    // Validate required fields
    if (!values.name || !values.name.en || !values.name.zh) {
      message.error('Please fill in both English and Chinese names');
      return;
    }

    if (!values.parent) {
      message.error('Please select a parent category');
      return;
    }

    // Generate slug from English name
    let slug = values.name.en
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();

    // Ensure slug is not empty and has minimum length
    if (!slug || slug.length < 2) {
      slug = `category-${Date.now()}`;
    }

    console.log('Generated slug:', slug);

    const categoryData = {
      name: values.name,
      slug: slug,
      description: values.description || { en: '', zh: '' },
      parent: values.parent,
      level: 1, // Always 1 for subcategories
      isActive: values.isActive !== undefined ? values.isActive : true,
      sortOrder: values.sortOrder || 0,
      image: categoryImage || undefined,
    }

    console.log('Sending category data:', categoryData);

    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory._id, data: categoryData })
    } else {
      createCategoryMutation.mutate(categoryData)
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
    setEditingCategory(null)
    setSelectedParent(null)
    setCategoryImage(null)
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <Title level={4} type="danger">Error Loading Categories</Title>
            <Text type="secondary">
              {(error as any)?.response?.data?.message || 'Failed to load categories'}
            </Text>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Title level={2} className="mb-2">Category Management</Title>
          <Text type="secondary">
            Manage product categories and subcategories. Main categories (Home Products, Medical Products) are protected.
          </Text>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleAdd()}
            size="large"
          >
            Add Subcategory
          </Button>
        </Space>
      </div>



      {/* Table View */}
      <Row>
        <Col span={24}>
          <Card>
            <Alert
              message="Protected Categories"
              description="Home Products and Medical Products are system categories that cannot be edited or deleted. You can only create subcategories under them."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              className="mb-4"
            />
            
            <Table
              columns={columns}
              dataSource={categories}
              loading={isLoading}
              rowKey="_id"
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} categories`
              }}
              expandable={{
                childrenColumnName: 'subcategories',
                indentSize: 20,
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Create New Category'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
            onClick={() => form.submit()}
          >
            {editingCategory ? 'Update' : 'Create'}
          </Button>,
        ]}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            sortOrder: 0
          }}
        >
          <Alert
            message="Category Information"
            description="Subcategories can only be created under main categories (Home Products or Medical Products). You cannot create subcategories under other subcategories."
            type="info"
            className="mb-4"
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['name', 'en']}
                label="Name (English)"
                rules={[{ required: true, message: 'Please enter English name' }]}
              >
                <Input placeholder="Enter category name in English" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['name', 'zh']}
                label="Name (Chinese)"
                rules={[{ required: true, message: 'Please enter Chinese name' }]}
              >
                <Input placeholder="輸入中文分類名稱" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['description', 'en']}
                label="Description (English)"
              >
                <TextArea rows={3} placeholder="Enter description in English" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['description', 'zh']}
                label="Description (Chinese)"
              >
                <TextArea rows={3} placeholder="輸入中文描述" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parent"
                label="Parent Category"
                rules={[{ required: true, message: 'Please select a parent category' }]}
              >
                <Select placeholder="Select parent category">
                  {mainCategories.map((cat: Category) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.name.en}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="sortOrder"
                label="Sort Order"
                tooltip="Lower numbers appear first"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="isActive"
                label="Active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="image"
                label="Category Image"
              >
                <div>
                  {categoryImage?.url && (
                    <div style={{ marginBottom: 16 }}>
                      <Image
                        src={categoryImage.url}
                        alt={categoryImage.alt.en}
                        style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 6 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7nOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg="
                      />
                      <Button 
                        icon={<DeleteOutlined />} 
                        size="small"
                        danger
                        style={{ marginLeft: 8 }}
                        onClick={() => {
                          setCategoryImage(null)
                          form.setFieldValue('image', null)
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  
                  <Upload
                    name="image"
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={handleCategoryImageUpload}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={uploadLoading}
                      size="large"
                      type="dashed"
                      style={{ width: '100%', height: categoryImage?.url ? 60 : 100 }}
                    >
                      <div style={{ marginTop: 8 }}>
                        {uploadLoading ? 'Uploading...' : categoryImage?.url ? 'Change Image' : 'Upload Category Image'}
                      </div>
                      {!categoryImage?.url && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                          Supports JPG, PNG, WebP
                        </div>
                      )}
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminCategories 